import type { ContractServiceConfigOption } from '@/api/contracts';
import { isStatementable } from './product-templates/statementable-templates';
import { vcloudConfigHint } from './product-templates/vcloud/vcloud-config-hint';

type Opt = ContractServiceConfigOption & { title?: string };

/** Semantic weight for a facet chip (maps to @ui Badge tones). */
export type FacetTone = 'neutral' | 'info' | 'success' | 'warning';

export interface ConfigFacet {
  /** Short label shown in the chip, e.g. "2 regions" or "Prepaid". */
  label: string;
  tone: FacetTone;
  /** Facet class — lets the compact renderer keep only the important ones. */
  kind: 'scale' | 'capacity' | 'model' | 'billing' | 'statement';
  /**
   * Plain-language clause for the narrative ("diễn giải") sentence, e.g.
   * "2 regions", "committing 1,000 GB", "billed upfront". Composed (not chip
   * labels) so the sentence reads smoothly. Same source of truth as `label`.
   */
  clause: string;
}

const num = (v: unknown) => Number(v ?? 0) || 0;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Human capacity, e.g. "1,000 GB". Groups by unit; only shown when > 0. */
function capacityFacet(opts: Opt[]): ConfigFacet | null {
  const total = opts.reduce((s, o) => s + num(o.commitment_value), 0);
  if (total <= 0) return null;
  const unit = opts.find((o) => num(o.commitment_value) > 0 && o.unit)?.unit ?? '';
  const value = total.toLocaleString('en-US');
  const label = unit ? `${value} ${unit}` : value;
  return { label, tone: 'neutral', kind: 'capacity', clause: `committing ${label}` };
}

/** Pricing model from `price_config.method`. Dominant across configs, or "Mixed". */
function modelFacet(opts: Opt[]): ConfigFacet | null {
  const methods = opts
    .map((o) => (o.price_config as { method?: string } | undefined)?.method)
    .filter((m): m is string => !!m);
  if (methods.length === 0) return null;
  const uniq = new Set(methods);
  const mixed = uniq.size > 1;
  const label = mixed ? 'Mixed model' : methods[0] === 'tierd' ? 'Tiered' : 'Progressive';
  const clause = mixed
    ? 'mixed pricing'
    : `${methods[0] === 'tierd' ? 'tiered' : 'progressive'} pricing`;
  return { label, tone: 'neutral', kind: 'model', clause };
}

/**
 * Billing terms — the facet a salesperson cares about most in the customer
 * convo. Prepaid (all true) / Postpaid (all false) / Partial prepaid (mixed).
 * Prepaid is the money-upfront signal, so it carries a success tone; partial a
 * warning (worth a second look).
 */
function billingFacet(opts: Opt[]): ConfigFacet | null {
  const flags = opts.map((o) => o.commitment_prepaid === true);
  if (flags.length === 0) return null;
  const allPrepaid = flags.every(Boolean);
  const nonePrepaid = flags.every((f) => !f);
  if (allPrepaid) return { label: 'Prepaid', tone: 'success', kind: 'billing', clause: 'billed upfront' };
  if (nonePrepaid) return { label: 'Postpaid', tone: 'neutral', kind: 'billing', clause: 'billed at renewal' };
  return {
    label: 'Partial prepaid',
    tone: 'warning',
    kind: 'billing',
    clause: 'partially billed upfront (remainder at renewal)',
  };
}

/**
 * Statement / đối soát — only meaningful for templates the backend reconciles
 * (isStatementable). Shown when any config carries the flag on; omitted entirely
 * for non-statementable templates so we never render a false "no statement".
 */
function statementFacet(templateName: string | undefined, opts: Opt[]): ConfigFacet | null {
  if (!isStatementable(templateName)) return null;
  const on = opts.some((o) => o.statementable === true);
  if (!on) return null;
  return {
    label: 'Statement',
    tone: 'info',
    kind: 'statement',
    clause: 'with monthly usage reconciliation',
  };
}

/** Scale facet (count of the product's primary billable units). */
function scaleFacet(templateName: string | undefined, opts: Opt[]): ConfigFacet | null {
  const mk = (label: string, clause = label): ConfigFacet => ({ label, tone: 'neutral', kind: 'scale', clause });
  switch (templateName) {
    case 'Cloud.VCloud':
      // VCloud narrates its own scale phrase (mode + per-group counts).
      return vcloudScale(opts);
    case 'SwiftFederation.CDN': {
      const regions = opts.filter((o) => o.type === 'bandwidth' || o.type === 'base-config').length;
      return regions > 0 ? mk(plural(regions, 'region')) : null;
    }
    case 'Server.VBM':
    case 'Device.Liquidation':
      return opts.length > 0 ? mk(plural(opts.length, 'device')) : null;
    case 'SwiftFederation.Transcoding':
    case 'SwiftFederation.Recording':
    case 'SwiftFederation.Transmuxing':
    case 'SwiftFederation.StorageOSS':
    case 'SwiftFederation.StorageLFD':
      return opts.length > 0 ? mk(plural(opts.length, 'option')) : null;
    default:
      return null;
  }
}

/**
 * VCloud scale facet. Chip keeps the terse "Predefined · 2 VMs" glance; the
 * narrative clause reads naturally, e.g. "2 predefined VM instances and 1 load
 * balancer".
 */
function vcloudScale(opts: Opt[]): ConfigFacet | null {
  const label = vcloudConfigHint(opts);
  if (!label) return null;

  const vms = opts.filter((o) => (o.title ?? 'vm-instance') === 'vm-instance').length;
  const lbs = opts.filter((o) => o.title === 'vm-lb').length;
  const others = opts.filter((o) => o.title === 'vm-other').length;
  const mode = opts.some((o) => o.unit === 'custom') ? 'custom' : 'predefined';

  const groups: string[] = [];
  if (vms > 0) groups.push(`${vms} ${mode} ${vms === 1 ? 'VM instance' : 'VM instances'}`);
  if (lbs > 0) groups.push(plural(lbs, 'load balancer'));
  if (others > 0) groups.push(plural(others, 'add-on'));
  const clause = joinList(groups) || label;

  return { label, tone: 'neutral', kind: 'scale', clause };
}

/** Join list items with commas and a trailing "and": [a,b,c] → "a, b and c". */
function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Item-level subject phrase — names the ACTUAL configured options, grouped, with
 * real identifiers + quantity/capacity, for the rich Total-section narrative.
 * e.g. "1× v2g-memory-2-4 VM, 1× lb1-1-2 load balancer and 2,000 GB backup" or
 * "2 regions committing 1,000 GB". Falls back to the terse scale phrase when no
 * option carries a name. Returns '' when nothing is configured.
 */
function itemDetailSubject(templateName: string | undefined, opts: Opt[]): string {
  // A row only enters the DEAL narrative once it has a package chosen (name).
  // Unconfigured placeholder rows are omitted — the readiness footer already
  // nudges the user to fill them, and a bare "1" reads hollow/wrong here.
  const qtyName = (o: Opt, noun: string) => {
    const name = (o.name ?? '').trim();
    if (!name) return '';
    return `${num(o.commitment_value) || 1}× ${name} ${noun}`;
  };

  if (templateName === 'Cloud.VCloud') {
    const items: string[] = [];
    for (const o of opts.filter((o) => (o.title ?? 'vm-instance') === 'vm-instance')) items.push(qtyName(o, 'VM'));
    for (const o of opts.filter((o) => o.title === 'vm-lb')) items.push(qtyName(o, 'load balancer'));
    // Other services are capacity-style: "2,000 GB backup" (real unit), else
    // just the package name; skipped entirely until a package is chosen.
    for (const o of opts.filter((o) => o.title === 'vm-other')) {
      const name = (o.name ?? '').trim();
      if (!name) continue;
      const cap = num(o.commitment_value);
      const hasUnit = !!o.unit && o.unit !== 'package';
      items.push(cap > 0 && hasUnit ? `${cap.toLocaleString('en-US')} ${o.unit} ${name}` : name);
    }
    return joinList(items.filter(Boolean));
  }

  if (templateName === 'SwiftFederation.CDN') {
    const regions = opts.filter((o) => o.type === 'bandwidth' || o.type === 'base-config').length;
    const total = opts.reduce((s, o) => s + num(o.commitment_value), 0);
    const unit = opts.find((o) => num(o.commitment_value) > 0 && o.unit)?.unit ?? '';
    const scale = plural(regions, 'region');
    return total > 0 ? `${scale} committing ${total.toLocaleString('en-US')}${unit ? ` ${unit}` : ''}` : scale;
  }

  // Transcoding / VBM / device / storage: name each CONFIGURED option × qty.
  const noun = templateName === 'Server.VBM' || templateName === 'Device.Liquidation' ? 'device' : 'option';
  return joinList(opts.map((o) => qtyName(o, noun)).filter(Boolean));
}

/** Human date "22 Jul 2026" from an ISO/`yyyy-mm-dd` string; '' if unparseable. */
function humanDate(s: string | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Months between two date strings (rounded); 0 if either missing/invalid. */
function monthsBetween(start: string | undefined, end: string | undefined): number {
  if (!start || !end) return 0;
  const a = new Date(start), b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const days = (b.getTime() - a.getTime()) / 86_400_000;
  return Math.max(0, Math.round(days / 30.4375));
}

export interface PaymentTimeline {
  /** Service-time period fields on `products[i]`. */
  first_startdate?: string;
  first_enddate?: string;
  continue_startdate?: string;
  continue_enddate?: string;
}

/**
 * Payment-timeline line for the Total section:
 * "First period 22 Jul 2026 – 21 Jul 2027 · renews yearly". Cadence from the
 * renewal period length (≈12 mo → "yearly", else "every N months"), falling back
 * to the first-period length. Returns null when the first period has no dates.
 */
export function paymentTimelineLine(dates: PaymentTimeline): string | null {
  const start = humanDate(dates.first_startdate);
  const end = humanDate(dates.first_enddate);
  if (!start && !end) return null;

  const period = start && end ? `First period ${start} – ${end}` : `First period ${start || end}`;

  const renewMonths =
    monthsBetween(dates.continue_startdate, dates.continue_enddate) ||
    monthsBetween(dates.first_startdate, dates.first_enddate);
  let cadence = '';
  if (renewMonths >= 11 && renewMonths <= 13) cadence = 'renews yearly';
  else if (renewMonths > 0) cadence = `renews every ${renewMonths} months`;

  return cadence ? `${period} · ${cadence}` : period;
}

/**
 * Structured config summary for a product line, dispatched by `template_name`.
 * A salesperson scans these facets to confirm the DEAL without expanding the
 * card: scale → capacity → pricing model → billing terms → statement. VCloud
 * folds its mode+VM count into the scale facet. Read-only over
 * `service_configoptions`; empty array = nothing to show yet.
 */
export function productConfigFacets(
  templateName: string | undefined,
  options: Opt[] | undefined,
): ConfigFacet[] {
  const opts = Array.isArray(options) ? options : [];
  if (opts.length === 0) return [];

  // Order = read priority. VCloud's scale already carries its mode; skip the
  // capacity/model/statement facets that don't apply to it.
  const facets = [
    scaleFacet(templateName, opts),
    templateName === 'Cloud.VCloud' ? null : capacityFacet(opts),
    templateName === 'Cloud.VCloud' ? null : modelFacet(opts),
    billingFacet(opts),
    statementFacet(templateName, opts),
  ];
  return facets.filter((f): f is ConfigFacet => f !== null);
}

/**
 * Flat one-line variant, joined with " · ". Full detail by default; pass
 * `compact` for space-constrained rows (Total-section breakdown), which keeps
 * only the priority facets — scale + billing + statement — dropping capacity and
 * pricing-model so the line stays short. Returns null when empty.
 */
export function productConfigHint(
  templateName: string | undefined,
  options: Opt[] | undefined,
  opts?: { compact?: boolean },
): string | null {
  let facets = productConfigFacets(templateName, options);
  if (opts?.compact) {
    facets = facets.filter((f) => f.kind === 'scale' || f.kind === 'billing' || f.kind === 'statement');
  }
  if (facets.length === 0) return null;
  return facets.map((f) => f.label).join(' · ');
}

/**
 * Narrative ("diễn giải") variant — one plain-language sentence a non-technical
 * salesperson or the customer understands at a glance. Reuses the same facet
 * derivation (DRY): scale + capacity form the subject phrase ("2 regions
 * committing 1,000 GB"), then pricing / billing / statement follow as comma
 * clauses. Omits any clause that doesn't apply, so it never reads hollow.
 * Returns null when there's nothing to describe yet.
 *
 * Examples:
 *   "2 regions committing 1,000 GB, progressive pricing, billed upfront, with monthly usage reconciliation."
 *   "1 predefined VM instance and 1 load balancer, billed upfront."
 */
export function productConfigNarrative(
  templateName: string | undefined,
  options: Opt[] | undefined,
  opts?: { itemLevel?: boolean },
): string | null {
  const list = Array.isArray(options) ? options : [];
  const facets = productConfigFacets(templateName, list);
  if (facets.length === 0) return null;

  const clauseFor = (kind: ConfigFacet['kind']) => facets.find((f) => f.kind === kind)?.clause;

  let subject: string;
  const trailing: (string | undefined)[] = [];

  if (opts?.itemLevel) {
    // Rich: name the actual configured items; mode (VCloud) becomes a clause.
    subject = itemDetailSubject(templateName, list);
    // Nothing configured yet (no packages chosen) → no deal narrative at all,
    // rather than a hollow "predefined packages, billed upfront" with no items.
    if (!subject) return null;
    if (templateName === 'Cloud.VCloud') {
      trailing.push(list.some((o) => o.unit === 'custom') ? 'custom quota' : 'predefined packages');
    } else {
      trailing.push(clauseFor('model'));
    }
  } else {
    // Terse: scale + "committing <capacity>", then pricing model.
    subject = [clauseFor('scale'), clauseFor('capacity')].filter(Boolean).join(' ');
    trailing.push(clauseFor('model'));
  }

  trailing.push(clauseFor('billing'), clauseFor('statement'));

  const rest = trailing.filter(Boolean);
  const sentence = [subject, ...rest].filter(Boolean).join(', ');
  if (!sentence) return null;
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}
